import type { Point } from '@ikegps/core-api';
import { action, makeObservable } from 'mobx';
import type { IkeDraw } from '../../draw';
import type { SVGEventContext } from './svg.event.context';
import { SVGEventTranslation } from './svg.event.translation';

export class SVGMove {
	startPoint: Point;
	lastPoint: Point;
	delta: Point;

	moving: boolean;

	pt: SVGPoint;
	ctm: SVGMatrix;

	eventName: string;
	dc: IkeDraw;
	ctx: SVGEventContext;
	svg: SVGSVGElement;

	// Move functions bound to 'this' SVGMove
	boundMoveTick: any;
	boundMove: any;
	boundEnd: any;

	onMove?: () => void;
	onEnd?: () => void;
	onStart?: () => void;

	constructor(dc: IkeDraw, ctx: SVGEventContext, svg: SVGSVGElement) {
		makeObservable(this);
		this.dc = dc;
		this.ctx = ctx;
		this.svg = svg;
	}

	start() {
		const dc = this.dc;

		this.startPoint = { x: 0, y: 0 };
		this.lastPoint = { x: 0, y: 0 };
		this.delta = { x: 0, y: 0 };

		this.pt = this.svg.createSVGPoint();
		this.ctm = this.svg.getScreenCTM();
		this.moving = true;

		this.pt.x = this.ctx.$event.clientX;
		this.pt.y = this.ctx.$event.clientY;

		const current = SVGEventTranslation.translatePoint(this.pt, this.ctm);

		this.lastPoint.x = this.startPoint.x = current.x;
		this.lastPoint.y = this.startPoint.y = current.y;

		this.boundEnd = this.end.bind(this);
		const $window = dc.window;
		$window.addEventListener('mouseup', this.boundEnd);
		$window.addEventListener('touchend', this.boundEnd);

		this.boundMove = this.move.bind(this);
		this.svg.addEventListener('mousemove', this.boundMove);
		this.svg.addEventListener('touchmove', this.boundMove);

		if (this.onStart) {
			this.onStart();
		}

		this.boundMoveTick = this.moveTick.bind(this);
		$window.requestAnimationFrame(this.boundMoveTick);
	}

	static create(dc: IkeDraw, ctx: SVGEventContext): SVGMove | undefined {
		if (ctx.$event.which !== 1) {
			return undefined;
		}

		const svg = SVGEventTranslation.getSVG(<Node>ctx.$event.target);
		if (svg == null) {
			return undefined;
		}
		return new SVGMove(dc, ctx, svg);
	}

	moveTick() {
		const dc = this.dc;
		const $window = dc.window;

		const current = SVGEventTranslation.translatePoint(this.pt, this.ctm);
		this.delta.x = current.x - this.lastPoint.x;
		this.delta.y = current.y - this.lastPoint.y;

		this.lastPoint.x = current.x;
		this.lastPoint.y = current.y;

		if (this.delta.x !== 0 || this.delta.y !== 0) {
			if (this.onMove) {
				this.onMove();
			}
		}

		if (!this.moving) {
			return;
		}
		$window.requestAnimationFrame(this.boundMoveTick);
	}

	@action.bound end(event: MouseEvent) {
		const dc = this.dc;
		const $window = dc.window;

		this.svg.removeEventListener('mousemove', this.boundMove);
		this.svg.removeEventListener('touchmove', this.boundMove);

		$window.removeEventListener('mouseup', this.boundEnd);
		$window.removeEventListener('touchend', this.boundEnd);

		this.pt.x = event.clientX;
		this.pt.y = event.clientY;

		const current = SVGEventTranslation.translatePoint(this.pt, this.ctm);

		this.lastPoint.x = current.x;
		this.lastPoint.y = current.y;
		this.moving = false;
		dc.state.moveId = null;

		if (this.onEnd) {
			this.onEnd();
		}
	}

	move(event: MouseEvent) {
		this.pt.x = event.clientX;
		this.pt.y = event.clientY;
	}
}
